import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IStore } from 'src/redux/reducers';
import { useFocusEffect } from '@react-navigation/native';
import {
  Box,
  Button,
  HStack,
  ScrollView,
  Text,
  Heading,
  VStack,
  Badge,
  Divider,
  AlertDialog,
  useToast,
} from 'native-base';
import { fetchUserSubscriptions, deleteSubscription } from 'src/redux/actions/subscriptionAction';
import { getText } from 'src/utils/i18n';

const ManageUserSubscription: React.FC<any> = ({ navigation }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const cancelRef = useRef(null);
  const userProfile = useSelector((state: IStore) => state.user.userProfile.response);
  const subscriptionState = useSelector((state: IStore) => state.subscription.userSubs);
  const createSubscriptionState = useSelector((state: IStore) => state.subscription.createSubscription);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Use subscription API response directly - it includes usage info and is already filtered
  // to show only the highest tier subscription per product type
  const subscriptions = subscriptionState.data || [];
  const activeSubscriptions = subscriptions.filter(
    sub => sub.status === 'SUBSCRIPTION_ACTIVE' || sub.status === 'SUBCRIPTION_ACTIVE'
  );

  const username = useSelector(
    (s: IStore) => s.user.userProfile.response?.username,
  );

  // Fetch user subscriptions when component mounts or username changes
  useEffect(() => {
    if (username) {
      dispatch(fetchUserSubscriptions.request({ username }));
    }
  }, [dispatch, username]);

  // Refresh subscriptions when screen comes into focus (e.g., after payment)
  useFocusEffect(
    React.useCallback(() => {
      if (username) {
        dispatch(fetchUserSubscriptions.request({ username }));
      }
    }, [dispatch, username])
  );

  // Refresh subscriptions when a subscription is successfully created
  useEffect(() => {
    if (createSubscriptionState.data && username) {
      dispatch(fetchUserSubscriptions.request({ username }));
    }
  }, [createSubscriptionState.data, dispatch, username]);

  // Handle subscription deletion success/failure
  useEffect(() => {
    if (isCancelling && selectedSubscription) {
      const subscriptionId = selectedSubscription.subscriptionId || selectedSubscription.id;
      const subscriptionStillExists = subscriptionState.data.some(
        sub => (sub.subscriptionId === subscriptionId || sub.id === subscriptionId)
      );
      
      // If we were cancelling and the subscription is no longer in the list, it was successful
      if (!subscriptionStillExists) {
        toast.show({
          title: "Subscription Cancelled",
          description: `Your ${getProductDisplayName(selectedSubscription.productType)} subscription has been cancelled.`,
          duration: 3000,
        });
        
        // Close dialog and reset state
        setIsConfirmDialogOpen(false);
        setSelectedSubscription(null);
        setIsCancelling(false);
      }
    }
  }, [subscriptionState.data, isCancelling, selectedSubscription, toast]);

  // Handle subscription errors
  useEffect(() => {
    if (isCancelling && subscriptionState.error) {
      toast.show({
        title: "Error",
        description: "Failed to cancel subscription. Please try again.",
        duration: 3000,
      });
      setIsCancelling(false);
    }
  }, [subscriptionState.error, isCancelling, toast]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatTimeAgo = (timestamp: number) => {
    const now = new Date();
    const date = new Date(timestamp * 1000);
    const diffInMs = date.getTime() - now.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays >= 0) {
      return `Next billing date: ${formatDate(timestamp)}`;
    } else {
      return `Expired ${Math.abs(diffInDays)} days ago`;
    }
  };

  const getProductDisplayName = (productType: string) => {
    switch (productType) {
      case 'GRAIN_ANALYSIS':
        return 'Grain Analysis';
      case 'SEED_COUNTER':
        return 'Seed Counter';
      default:
        return productType;
    }
  };

  const getStatusDisplayName = (status: string) => {
    switch (status) {
      case 'SUBSCRIPTION_ACTIVE':
      case 'SUBCRIPTION_ACTIVE':
        return 'Active';
      case 'SUBCRIPTION_UNKNOWN':
        return 'Unknown';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUBSCRIPTION_ACTIVE':
      case 'SUBCRIPTION_ACTIVE':
        return 'success';
      case 'SUBCRIPTION_UNKNOWN':
        return 'warning';
      default:
        return 'gray';
    }
  };

  const handleCancelSubscription = (subscription: any) => {
    setSelectedSubscription(subscription);
    setIsConfirmDialogOpen(true);
  };

  const confirmCancelSubscription = () => {
    if (!selectedSubscription || !username) return;
    
    setIsCancelling(true);
    
    // Dispatch the delete subscription action
    dispatch(deleteSubscription.request({ 
      username, 
      subscriptionId: selectedSubscription.subscriptionId || selectedSubscription.id 
    }));
  };

  const handleCancelConfirmation = () => {
    setIsConfirmDialogOpen(false);
    setSelectedSubscription(null);
  };

  return (
    <ScrollView flex={1} p={4}>
      <Heading mb={3}>{getText('manageSubscriptionHeading')}</Heading>

      <VStack space={4}>
        <Box p={4} borderWidth={1} borderColor="gray.300" borderRadius="md">
          <Text fontSize="lg" mb={2}>{getText('usernameInfoLabel')}: {userProfile?.username}</Text>
          <Text>{getText('organizationLabel')}: {userProfile?.org?.name || '-'}</Text>
          <Text>{getText('centerLabel')}: {userProfile?.center?.name || '-'}</Text>
        </Box>

        <Box p={4} borderWidth={1} borderColor="gray.300" borderRadius="md">
          <Heading size="md" mb={3}>{getText('activeSubscriptionsHeading')} ({activeSubscriptions.length})</Heading>

          {activeSubscriptions.length === 0 ? (
            <Text color="gray.500">{getText('noActiveSubscriptions')}</Text>
          ) : (
            <VStack space={3}>
              {activeSubscriptions.map((subscription, index) => (
                <Box key={subscription.id || index} p={3} bg="gray.50" borderRadius="md">
                  <HStack justifyContent="space-between" alignItems="center" mb={2}>
                    <Text fontSize="md" fontWeight="bold">
                      {getProductDisplayName(subscription.productType)}
                    </Text>
                    <Badge colorScheme={getStatusColor(subscription.status)}>
                      {getStatusDisplayName(subscription.status)}
                    </Badge>
                  </HStack>

                  <VStack space={1} mb={3}>
                    {subscription.planID && (
                      <Text fontSize="sm">{getText('planLabel')}: {subscription.planID}</Text>
                    )}
                    <Text fontSize="sm">{formatTimeAgo(subscription.expiry)}</Text>
                    {subscription.numberOfMonths && (
                      <Text fontSize="sm">{getText('durationLabel')}: {subscription.numberOfMonths} months</Text>
                    )}
                  </VStack>

                  {/* Usage Information */}
                  {subscription.usage && subscription.usage.total && (
                    <Box p={3} bg="blue.50" borderRadius="md" mb={3}>
                      <Heading size="xs" mb={2}>{getText('usageStatistics')}</Heading>
                      <VStack space={2}>
                        <HStack justifyContent="space-between">
                          <Text fontSize="sm" fontWeight="medium">{getText('dailyUsageLabel')}:</Text>
                          <Text fontSize="sm">
                            {subscription.usage.total.dailyUsage} / {subscription.usage.total.dailyLimit}
                          </Text>
                        </HStack>
                        <HStack justifyContent="space-between">
                          <Text fontSize="sm" fontWeight="medium">{getText('monthlyUsageLabel')}:</Text>
                          <Text fontSize="sm">
                            {subscription.usage.total.monthlyUsage} / {subscription.usage.total.monthlyLimit}
                          </Text>
                        </HStack>
                        {subscription.usage.grains && subscription.usage.grains.length > 0 && (
                          <VStack space={1} mt={2}>
                            <Text fontSize="xs" color="gray.600">{getText('perGrainUsageLabel')}:</Text>
                            {subscription.usage.grains.map((grain, idx) => (
                              <HStack key={idx} justifyContent="space-between" pl={2}>
                                <Text fontSize="xs">Grain {grain.grainId}:</Text>
                                <Text fontSize="xs">
                                  {grain.monthlyUsage} / {grain.monthlyLimit}
                                </Text>
                              </HStack>
                            ))}
                          </VStack>
                        )}
                      </VStack>
                    </Box>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="danger"
                    onPress={() => handleCancelSubscription(subscription)}
                    isDisabled={isCancelling}
                  >
                    {getText('cancelSubscriptionButton')}
                  </Button>
                </Box>
              ))}
            </VStack>
          )}
        </Box>

        <HStack space={3}>
          <Button
            flex={1}
            onPress={() => navigation.navigate('UserSubscriptionPlans')}
            isDisabled={!username}
          >
            {getText('changeSubscription')}
          </Button>
          <Button
            flex={1}
            variant="outline"
            onPress={() => navigation.navigate('Home')}
          >
            {getText('continueToHome')}
          </Button>
        </HStack>
      </VStack>

      {/* Cancellation Confirmation Dialog */}
      <AlertDialog
        isOpen={isConfirmDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleCancelConfirmation}
      >
        <AlertDialog.Content>
          <AlertDialog.CloseButton />
          <AlertDialog.Header>{getText('cancelSubscriptionDialogTitle')}</AlertDialog.Header>
          <AlertDialog.Body>
            {selectedSubscription && (
              <VStack space={2}>
                <Text>
                  Are you sure you want to cancel your{' '}
                  <Text bold>{getProductDisplayName(selectedSubscription.productType)}</Text>{' '}
                  subscription?
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Plan ID: {selectedSubscription.planID || selectedSubscription.id}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {formatTimeAgo(selectedSubscription.expiry)}
                </Text>
                <Text fontSize="sm" color="red.600" mt={2}>
                  {getText('cancelSubscriptionWarning')}
                </Text>
              </VStack>
            )}
          </AlertDialog.Body>
          <AlertDialog.Footer>
            <Button.Group space={2}>
              <Button
                ref={cancelRef}
                variant="unstyled"
                colorScheme="coolGray"
                onPress={handleCancelConfirmation}
                isDisabled={isCancelling}
              >
                {getText('keepSubscription')}
              </Button>
              <Button
                colorScheme="danger"
                onPress={confirmCancelSubscription}
                isLoading={isCancelling}
                isLoadingText={getText('cancelling')}
              >
                {getText('cancelSubscriptionButton')}
              </Button>
            </Button.Group>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </ScrollView>
  );
};

export default ManageUserSubscription;
